import React, { useEffect, useMemo, useState } from 'react';
import FitTable from '../components/FitTable';
import PlayerName from '../components/PlayerName';
import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-react';

/* ─── Types ───────────────────────────────────────────────────── */

interface Season {
  season_id: number;
  season_year: string;
  season_start?: string;
  season_end?: string;
}

interface CoursePop {
  course_name: string;
  facility_name?: string | null;
  rounds: number;
  player_rounds: number;
}

interface BestPerf {
  first_name: string;
  last_name: string;
  gross: number;
  net: number;
  date: string;
  course_name: string;
  is_major: boolean;
  season_year?: string; // present on the all-time list
}

interface MajorTitleRow {
  region: string;
  player_id: number;
  first_name: string;
  last_name: string;
  wins: number;
  years: number[]; // winning year per title (chronological)
}

interface MajorTitles {
  regions: string[];
  rows: MajorTitleRow[];
}

interface LeagueTitleRow {
  player_id: number;
  first_name: string;
  last_name: string;
  titles: number;
  years: string[];
}

// Subset of the /api/standings response we need to find each season's champion.
interface StandingRow {
  player_id: number;
  first_name: string;
  last_name: string;
  total_points: number;
}

interface PlayerLite {
  player_id: number;
  first_name: string;
  last_name: string;
  league_status: string;
  user_id: string | null;
}

interface TrendRound {
  round_id: number;
  date: string;
  course_name: string;
  number_of_holes: number;
  players: number;
  avg_gross: number;
  avg_net: number;
  low_net: number;
}

interface LeagueInsights {
  summary: {
    total_rounds?: number;
    active_players?: number;
    avg_gross?: number;
    avg_net?: number;
  };
  lowest_net: {
    net_score: number;
    first_name: string;
    last_name: string;
    date: string;
    course_name: string;
  } | null;
  score_trend: TrendRound[];
  courses: CoursePop[];
  best_performances: BestPerf[];
}

interface TimelineRound {
  round_id: number;
  date: string;
  course_name: string;
  number_of_holes: number;
  is_major: boolean;
  tournament_name: string | null;
  gross_score: number;
  net_score: number;
  course_handicap: number;
  is_disqualified: boolean;
  rank: number;
  field_size: number;
}

interface PlayerInsights {
  player: {
    player_id: number;
    first_name: string;
    last_name: string;
    handicap: number;
    tournament_handicap: number;
    season_handicap?: number | null;            // performance handicap for the viewed season
    season_handicap_method?: 'index' | 'avg' | null;
  };
  timeline: TimelineRound[];
}

// One aggregated row per season for the year-over-year ("All seasons") views.
interface ManGame { size: number; rounds: number }

interface LeagueSeasonAgg {
  season_year: string;
  reg_rounds: number;
  major_rounds: number;
  total_rounds: number;
  active_players: number;
  majors: number;
  major_participants: number;
  avg_gross: number | null;
  avg_net: number | null;
  low_gross: number | null;
  low_net: number | null;
  man_games: ManGame[];
}

interface BiggestRound {
  season_year: string;
  players: number;
  date: string;
  course_name: string;
  is_major: boolean;
}

interface LeagueYoY {
  seasons: LeagueSeasonAgg[];
  biggest_round: BiggestRound | null;
  courses: CoursePop[];           // all-time course popularity
  best_performances: BestPerf[];  // all-time lowest gross
}

interface PlayerSeasonAgg {
  season_year: string;
  rounds_played: number;
  reg_rounds: number;
  major_rounds: number;
  wins: number;
  reg_wins: number;
  major_wins: number;
  podiums: number;
  avg_gross: number | null;
  avg_net: number | null;
  best_net: number | null;
  best_gross: number | null;
  handicap: number | null;                  // performance handicap for the season (null = none)
  handicap_method: 'index' | 'avg' | null;  // how it was derived
}

// One member's handicap index for one season (league overlay chart).
interface HcpSeasonRow {
  player_id: number;
  first_name: string;
  last_name: string;
  season_year: string;
  handicap: number;
}

type Tab = 'league' | 'players' | 'titles';
type ScoreMode = 'net' | 'gross';

// Sentinel season value that switches both tabs into year-over-year mode.
const ALL_SEASONS = 'all';

/* ─── Helpers ─────────────────────────────────────────────────── */

// pg returns numeric/bigint columns as strings — coerce defensively.
const num = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const fmtDate = (d: string | null | undefined) => {
  if (!d) return '—';
  const m = d.slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return d;
  const dt = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return isNaN(dt.getTime()) ? d : dt.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
};

const medalFor = (r: number) => (r === 1 ? '🥇' : r === 2 ? '🥈' : r === 3 ? '🥉' : `#${r}`);
const formClass = (r: number) => (r === 1 ? 'ins-p1' : r === 2 ? 'ins-p2' : r === 3 ? 'ins-p3' : 'ins-pr');
const perfName = (b: BestPerf) => `${b.first_name} ${b.last_name}`;

/* ─── Reusable inline-SVG line chart (no external dependency) ──── */

interface ChartPoint {
  label: string;   // x-axis label (date)
  value: number;   // y value
  tooltip: string; // hover text
  marked?: boolean; // emphasise this point (e.g. a major)
}

const LineChart: React.FC<{
  data: ChartPoint[];
  series2?: number[];   // optional second line (e.g. gross), aligned to data by index
  color?: string;
  color2?: string;
  lowerIsBetter?: boolean;
}> = ({ data, series2, color = '#2563eb', color2 = '#94a3b8', lowerIsBetter = true }) => {
  // Hover previews a point; clicking pins it so the value stays on screen.
  const [hover, setHover] = useState<number | null>(null);
  const [pinned, setPinned] = useState<number | null>(null);

  if (data.length === 0) return <div className="ins-chart-empty">No rounds to chart yet.</div>;

  const W = 720;
  const H = 250;
  const padL = 40;
  const padR = 16;
  const padT = 18;
  const padB = 36;

  const hasS2 = !!series2 && series2.length === data.length;
  const values = data.map((d) => d.value);
  const allValues = hasS2 ? values.concat(series2 as number[]) : values;
  let min = Math.min(...allValues);
  let max = Math.max(...allValues);
  if (min === max) { min -= 1; max += 1; }
  const margin = (max - min) * 0.15 || 1;
  min -= margin;
  max += margin;

  const x = (i: number) =>
    data.length === 1
      ? padL + (W - padL - padR) / 2
      : padL + (i / (data.length - 1)) * (W - padL - padR);
  const y = (v: number) => padT + (1 - (v - min) / (max - min)) * (H - padT - padB);

  const linePath = data.map((d, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(d.value).toFixed(1)}`).join(' ');
  const areaPath = `${linePath} L${x(data.length - 1).toFixed(1)},${H - padB} L${x(0).toFixed(1)},${H - padB} Z`;
  const line2Path = hasS2
    ? (series2 as number[]).map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ')
    : '';

  // Three horizontal gridlines (min / mid / max of the padded range).
  const ticks = [min, (min + max) / 2, max];

  // Show at most ~7 x-axis labels to avoid crowding.
  const step = Math.max(1, Math.ceil(data.length / 7));

  // Highlight the single best primary value — only on a single-series chart,
  // where the green callout can't be confused with a second line.
  const bestIdx = hasS2 ? -1 : values.indexOf(lowerIsBetter ? Math.min(...values) : Math.max(...values));

  // Hover wins over a pin so moving the mouse always previews the value.
  const active = hover !== null ? hover : pinned;

  // Build the value tooltip (a dark rounded box) for the active point.
  let tip: React.ReactNode = null;
  if (active !== null) {
    const lines = data[active].tooltip.split('\n');
    const tw = Math.min(W - 4, Math.max(...lines.map((l) => l.length)) * 6.4 + 18);
    const th = lines.length * 15 + 14;
    const ax = x(active);
    const ay = y(data[active].value);
    const tx = Math.max(2, Math.min(W - tw - 2, ax - tw / 2));
    const ty = ay - th - 14 < 2 ? ay + 16 : ay - th - 14;
    tip = (
      <g pointerEvents="none">
        <rect x={tx} y={ty} width={tw} height={th} rx={7} fill="#0f172a" opacity={0.96} />
        {lines.map((ln, k) => (
          <text
            key={k}
            x={tx + 9}
            y={ty + 17 + k * 15}
            fontSize={11.5}
            fontWeight={k === 0 ? 700 : 500}
            fill={k === 0 ? '#fff' : '#cbd5e1'}
          >
            {ln}
          </text>
        ))}
      </g>
    );
  }

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="xMidYMid meet" role="img">
      <defs>
        <linearGradient id="ins-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.18" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>

      {ticks.map((t, i) => (
        <g key={i}>
          <line x1={padL} y1={y(t)} x2={W - padR} y2={y(t)} stroke="#eef2f7" strokeWidth={1} />
          <text x={padL - 6} y={y(t) + 3} textAnchor="end" fontSize={10} fill="#94a3b8">
            {t.toFixed(t % 1 === 0 ? 0 : 1)}
          </text>
        </g>
      ))}

      {!hasS2 && <path d={areaPath} fill="url(#ins-fill)" pointerEvents="none" />}
      {hasS2 && (
        <path d={line2Path} fill="none" stroke={color2} strokeWidth={2} strokeDasharray="5 3" strokeLinejoin="round" strokeLinecap="round" pointerEvents="none" />
      )}
      <path d={linePath} fill="none" stroke={color} strokeWidth={2.25} strokeLinejoin="round" strokeLinecap="round" pointerEvents="none" />

      {active !== null && (
        <line x1={x(active)} y1={padT} x2={x(active)} y2={H - padB} stroke="#cbd5e1" strokeWidth={1} strokeDasharray="3 3" pointerEvents="none" />
      )}

      {data.map((d, i) => {
        const isActive = active === i;
        const r = isActive ? 6 : d.marked || i === bestIdx ? 4.5 : 3;
        const fill = d.marked ? '#f59e0b' : i === bestIdx ? '#16a34a' : isActive ? color : '#fff';
        const stroke = d.marked ? '#f59e0b' : i === bestIdx ? '#16a34a' : color;
        return (
          <g key={i}>
            {hasS2 && (
              <circle cx={x(i)} cy={y((series2 as number[])[i])} r={isActive ? 5 : 3} fill={isActive ? color2 : '#fff'} stroke={color2} strokeWidth={1.5} pointerEvents="none" />
            )}
            <circle cx={x(i)} cy={y(d.value)} r={r} fill={fill} stroke={stroke} strokeWidth={1.75} pointerEvents="none" />
            {/* Generous transparent hit target so dots are easy to hover/tap. */}
            <circle
              cx={x(i)}
              cy={y(d.value)}
              r={16}
              fill="transparent"
              style={{ cursor: 'pointer' }}
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
              onClick={() => setPinned((p) => (p === i ? null : i))}
            />
            {i % step === 0 || i === data.length - 1 ? (
              <text x={x(i)} y={H - padB + 16} textAnchor="middle" fontSize={10} fill="#94a3b8" pointerEvents="none">
                {d.label}
              </text>
            ) : null}
          </g>
        );
      })}

      {tip}
    </svg>
  );
};

/* ─── Participation bar strip ─────────────────────────────────── */

const Bars: React.FC<{ data: { label: string; value: number; tooltip: string }[] }> = ({ data }) => {
  if (data.length === 0) return <div className="ins-chart-empty">No rounds yet.</div>;
  const max = Math.max(...data.map((d) => d.value), 1);
  const step = Math.max(1, Math.ceil(data.length / 8));
  return (
    <div className="ins-bars">
      {data.map((d, i) => (
        <div key={i} className="ins-bar-col" title={d.tooltip}>
          <div className="ins-bar-track">
            <div className="ins-bar-fill" style={{ height: `${(d.value / max) * 100}%` }}>
              <span className="ins-bar-val">{d.value}</span>
            </div>
          </div>
          <div className="ins-bar-label">{i % step === 0 || i === data.length - 1 ? d.label : ''}</div>
        </div>
      ))}
    </div>
  );
};

/* ─── Grouped bars: two bars per category + a total (e.g. reg vs major) ─ */

const GroupedBars: React.FC<{ data: { label: string; reg: number; major: number; tooltip: string }[] }> = ({ data }) => {
  if (data.length === 0) return <div className="ins-chart-empty">No rounds yet.</div>;
  const H = 150;
  const max = Math.max(...data.map((d) => Math.max(d.reg, d.major)), 1);
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', overflowX: 'auto', padding: '20px 4px 4px' }}>
      {data.map((d, i) => {
        const total = d.reg + d.major;
        return (
          <div key={i} title={d.tooltip} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, minWidth: 56, flex: '1 0 auto' }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: '#0f172a' }}>{total}</div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: H }}>
              {[{ v: d.reg, c: '#2563eb' }, { v: d.major, c: '#f59e0b' }].map((b, k) => (
                <div key={k} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%' }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: '#475569', lineHeight: 1 }}>{b.v > 0 ? b.v : ''}</span>
                  <div style={{ width: 15, height: `${(b.v / max) * 100}%`, minHeight: b.v > 0 ? 3 : 0, background: b.c, borderRadius: '3px 3px 0 0', marginTop: 2 }} />
                </div>
              ))}
            </div>
            <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>{d.label}</div>
          </div>
        );
      })}
    </div>
  );
};

/* ─── Multi-series line chart: one line per player, for the league overlay ─ */

const MULTI_PALETTE = [
  '#2563eb', '#dc2626', '#16a34a', '#d97706', '#7c3aed', '#0891b2',
  '#db2777', '#65a30d', '#ea580c', '#0d9488', '#9333ea', '#475569', '#b91c1c',
];

interface MultiSeries { name: string; values: (number | null)[] } // aligned to xLabels

const MultiLineChart: React.FC<{ xLabels: string[]; series: MultiSeries[]; lowerIsBetter?: boolean }> = ({ xLabels, series }) => {
  const [active, setActive] = useState<number | null>(null);
  if (xLabels.length === 0 || series.length === 0) return <div className="ins-chart-empty">No handicap data yet.</div>;

  const W = 720, H = 300, padL = 36, padR = 14, padT = 16, padB = 34;
  const all = series.flatMap((s) => s.values).filter((v): v is number => v != null);
  if (all.length === 0) return <div className="ins-chart-empty">No handicap data yet.</div>;
  let min = Math.min(...all), max = Math.max(...all);
  if (min === max) { min -= 1; max += 1; }
  const m = (max - min) * 0.15 || 1; min -= m; max += m;

  const x = (i: number) => xLabels.length === 1 ? padL + (W - padL - padR) / 2 : padL + (i / (xLabels.length - 1)) * (W - padL - padR);
  const y = (v: number) => padT + (1 - (v - min) / (max - min)) * (H - padT - padB);
  const ticks = [min, (min + max) / 2, max];

  // Build a path that connects a series' present points, skipping gaps.
  const pathFor = (vals: (number | null)[]) => {
    let dStr = ''; let started = false;
    vals.forEach((v, i) => {
      if (v == null) { started = false; return; }
      dStr += `${started ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)} `;
      started = true;
    });
    return dStr.trim();
  };

  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="xMidYMid meet" role="img"
        onMouseLeave={() => setActive(null)}>
        {ticks.map((t, i) => (
          <g key={i}>
            <line x1={padL} y1={y(t)} x2={W - padR} y2={y(t)} stroke="#eef2f7" strokeWidth={1} />
            <text x={padL - 6} y={y(t) + 3} textAnchor="end" fontSize={10} fill="#94a3b8">{t.toFixed(1)}</text>
          </g>
        ))}
        {xLabels.map((lab, i) => (
          <g key={i}>
            <rect x={x(i) - (W / xLabels.length) / 2} y={padT} width={W / xLabels.length} height={H - padT - padB}
              fill="transparent" onMouseEnter={() => setActive(i)} />
            <text x={x(i)} y={H - padB + 16} textAnchor="middle" fontSize={10} fill="#64748b">{lab}</text>
          </g>
        ))}
        {active !== null && (
          <line x1={x(active)} y1={padT} x2={x(active)} y2={H - padB} stroke="#cbd5e1" strokeWidth={1} strokeDasharray="3 3" />
        )}
        {series.map((s, si) => {
          const color = MULTI_PALETTE[si % MULTI_PALETTE.length];
          return (
            <g key={si}>
              <path d={pathFor(s.values)} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" opacity={0.9} />
              {s.values.map((v, i) => v != null ? (
                <circle key={i} cx={x(i)} cy={y(v)} r={active === i ? 4 : 2.5} fill={color} />
              ) : null)}
            </g>
          );
        })}
      </svg>
      {active !== null && (
        <div style={{ fontSize: 12, color: '#475569', margin: '4px 2px 0' }}>
          <strong>{xLabels[active]}:</strong>{' '}
          {series
            .map((s, si) => ({ s, si, v: s.values[active] }))
            .filter((r) => r.v != null)
            .sort((a, b) => (a.v as number) - (b.v as number))
            .map((r) => (
              <span key={r.si} style={{ marginRight: 10, whiteSpace: 'nowrap' }}>
                <i style={{ display: 'inline-block', width: 9, height: 9, borderRadius: 2, background: MULTI_PALETTE[r.si % MULTI_PALETTE.length], marginRight: 3, verticalAlign: 'middle' }} />
                {r.s.name} {r.v}
              </span>
            ))}
        </div>
      )}
      <div className="ins-legend" style={{ marginTop: 8 }}>
        {series.map((s, si) => (
          <span key={si}><i className="ins-key" style={{ background: MULTI_PALETTE[si % MULTI_PALETTE.length] }} /> {s.name}</span>
        ))}
      </div>
    </div>
  );
};

/* ─── Small building blocks ───────────────────────────────────── */

const StatCard: React.FC<{ label: string; value: React.ReactNode; sub?: React.ReactNode; accent?: string }> = ({
  label,
  value,
  sub,
  accent,
}) => (
  <div className="ins-stat">
    <div className="ins-stat-label">{label}</div>
    <div className="ins-stat-value" style={accent ? { color: accent } : undefined}>{value}</div>
    {sub ? <div className="ins-stat-sub">{sub}</div> : null}
  </div>
);

const FormStrip: React.FC<{ ranks: number[] }> = ({ ranks }) => {
  if (ranks.length === 0) return <span className="ins-muted">No rounds yet.</span>;
  return (
    <span className="ins-form">
      {ranks.map((r, i) => (
        <span key={i} className={`ins-dot ${formClass(r)}${i === ranks.length - 1 ? ' last' : ''}`} title={`Finished ${medalFor(r)}`}>
          {r}
        </span>
      ))}
    </span>
  );
};

const Card: React.FC<{ icon: string; title: string; right?: React.ReactNode; children: React.ReactNode }> = ({
  icon,
  title,
  right,
  children,
}) => (
  <div className="ins-card">
    <div className="ins-card-head">
      <span style={{ fontSize: 18 }}>{icon}</span>
      <h2 className="ins-card-title">{title}</h2>
      {right ? <div style={{ marginLeft: 'auto' }}>{right}</div> : null}
    </div>
    <div className="ins-card-body">{children}</div>
  </div>
);

const Toggle: React.FC<{ value: ScoreMode; onChange: (m: ScoreMode) => void }> = ({ value, onChange }) => (
  <div className="ins-seg ins-seg-sm">
    <button className={value === 'net' ? 'active' : ''} onClick={() => onChange('net')}>Net</button>
    <button className={value === 'gross' ? 'active' : ''} onClick={() => onChange('gross')}>Gross</button>
  </div>
);

/* ─── Scoped styles ───────────────────────────────────────────── */

const css = `
.ins-page{background:#f8fafc;min-height:100vh;padding:24px 16px;font-family:'Inter',-apple-system,BlinkMacSystemFont,sans-serif}
.ins-inner{width:100%;max-width:1120px;margin:0 auto}
.ins-top{display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:12px;margin-bottom:18px}
.ins-title{font-size:28px;font-weight:700;color:#1e293b;margin:0}
.ins-season{display:flex;align-items:center;gap:8px}
.ins-season span{font-size:14px;color:#64748b;font-weight:500}
.ins-select{border:1px solid #e2e8f0;border-radius:8px;padding:6px 12px;font-size:14px;color:#1e293b;background:#fff;cursor:pointer;outline:none}
.ins-seg{display:inline-flex;background:#f1f5f9;border-radius:10px;padding:4px;gap:4px}
.ins-seg button{border:none;background:transparent;color:#64748b;font-weight:700;font-size:14px;padding:8px 18px;border-radius:8px;cursor:pointer}
.ins-seg button.active{background:#fff;color:#1e40af;box-shadow:0 1px 3px rgba(0,0,0,.12)}
.ins-seg-sm button{font-size:12px;padding:5px 12px}
.ins-tabs{margin-bottom:20px}
.ins-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(150px,1fr));gap:14px;margin-bottom:22px}
.ins-stat{background:#fff;border:1px solid #e2e8f0;border-radius:12px;padding:16px 18px;box-shadow:0 1px 3px rgba(0,0,0,.05)}
.ins-stat-label{font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.05em;color:#94a3b8;margin-bottom:6px}
.ins-stat-value{font-size:26px;font-weight:800;color:#0f172a;letter-spacing:-.02em;line-height:1.1}
.ins-stat-sub{font-size:12px;color:#64748b;margin-top:5px}
.ins-card{background:#fff;border:1px solid #e2e8f0;border-radius:12px;box-shadow:0 1px 4px rgba(0,0,0,.06);margin-bottom:22px;overflow:hidden}
.ins-card-head{display:flex;align-items:center;gap:10px;padding:14px 18px;border-bottom:1px solid #f1f5f9}
.ins-card-title{font-size:16px;font-weight:700;color:#1e293b;margin:0}
.ins-card-body{padding:16px 18px}
.ins-chart-empty{padding:36px 12px;text-align:center;color:#94a3b8;font-size:14px}
.ins-legend{display:flex;gap:16px;flex-wrap:wrap;margin-top:10px;font-size:12px;color:#64748b}
.ins-legend span{display:inline-flex;align-items:center;gap:5px}
.ins-key{width:10px;height:10px;border-radius:50%;display:inline-block}
.ins-bars{display:flex;align-items:flex-end;gap:6px;height:150px;padding-top:8px}
.ins-bar-col{flex:1;display:flex;flex-direction:column;align-items:center;height:100%;min-width:0}
.ins-bar-track{flex:1;width:100%;display:flex;align-items:flex-end;justify-content:center}
.ins-bar-fill{width:70%;max-width:34px;background:linear-gradient(180deg,#60a5fa,#2563eb);border-radius:5px 5px 0 0;min-height:3px;position:relative;display:flex;justify-content:center}
.ins-bar-val{position:absolute;top:-16px;left:50%;transform:translateX(-50%);font-size:10px;font-weight:700;color:#475569}
.ins-bar-label{font-size:10px;color:#94a3b8;margin-top:5px;white-space:nowrap;height:12px}
.ins-stack-wrap{width:70%;max-width:34px;min-height:3px;position:relative;display:flex;flex-direction:column;justify-content:flex-end}
.ins-stack{flex:1;min-height:3px;display:flex;flex-direction:column-reverse;border-radius:5px 5px 0 0;overflow:hidden}
.ins-stack-seg{width:100%}
.ins-rank{display:flex;flex-direction:column;gap:9px}
.ins-rank-row{display:flex;align-items:center;gap:10px}
.ins-rank-name{flex:0 0 38%;max-width:38%;display:flex;flex-direction:column;gap:1px;overflow:hidden}
.ins-rank-course{font-size:13px;color:#1e293b;font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.ins-rank-fac{font-size:11px;color:#94a3b8;font-weight:500;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.ins-rank-bar{flex:1;height:14px;background:#f1f5f9;border-radius:7px;overflow:hidden}
.ins-rank-fill{display:block;height:100%;background:linear-gradient(90deg,#60a5fa,#2563eb);border-radius:7px;min-width:3px}
.ins-rank-val{flex:0 0 auto;font-size:13px;font-weight:700;color:#475569;min-width:22px;text-align:right}
.ins-picker{display:flex;align-items:center;gap:10px;flex-wrap:wrap;margin-bottom:22px}
.ins-picker label{font-size:13px;font-weight:600;color:#475569}
.ins-picker select{border:1px solid #e2e8f0;border-radius:10px;padding:9px 14px;font-size:15px;font-weight:600;color:#1e293b;background:#fff;cursor:pointer;outline:none;min-width:220px}
.ins-form{display:inline-flex;gap:4px;flex-wrap:wrap}
.ins-dot{display:inline-flex;align-items:center;justify-content:center;min-width:1.9em;height:1.9em;border-radius:7px;font-size:.86em;font-weight:800;padding:0 .45em}
.ins-dot.last{box-shadow:0 0 0 2px #fff,0 0 0 3px #cbd5e1}
.ins-p1{background:#fef3c7;color:#b45309}.ins-p2{background:#f1f5f9;color:#475569}.ins-p3{background:#fef0e7;color:#9a3412}.ins-pr{background:#dbeafe;color:#1d4ed8}
.ins-muted{color:#94a3b8;font-size:13px}
.ins-tbl th{background:#f8fafc;color:#64748b;border-bottom:1px solid #e2e8f0}
.ins-tbl td{border-bottom:1px solid #f1f5f9;color:#374151}
.ins-tbl tbody tr:hover{background:#f5f9ff}
.ins-tbl .r{text-align:right}
.ins-badge{display:inline-block;font-size:.8em;font-size:max(.8em,8px);font-weight:700;padding:.15em .6em;border-radius:6px}
.ins-badge-major{background:#fef3c7;color:#92400e}
.ins-badge-reg{background:#f1f5f9;color:#475569}
.ins-loading{display:flex;justify-content:center;padding:80px 20px;color:#64748b;font-size:16px}
.ins-err{padding:24px;color:#ef4444}
.ins-more{display:flex;gap:8px;flex-wrap:wrap;padding:12px 2px 2px}
.ins-more-btn{border:1px solid #e2e8f0;background:#fff;color:#1e40af;font-weight:700;font-size:13px;padding:7px 14px;border-radius:8px;cursor:pointer}
.ins-more-btn:hover{background:#f5f9ff}
.ins-more-btn.ghost{color:#64748b}
.ins-filters{display:flex;flex-wrap:wrap;gap:8px;align-items:center;margin-bottom:12px}
.ins-filters select{border:1px solid #e2e8f0;border-radius:8px;padding:6px 10px;font-size:13px;color:#1e293b;background:#fff;cursor:pointer;outline:none;max-width:200px}
@media (max-width:560px){
  .ins-page{padding:16px 12px}
  .ins-title{font-size:22px}
  .ins-seg button{padding:8px 12px;font-size:13px}
  .ins-seg-sm button{padding:5px 9px}
  .ins-rank-name{flex-basis:44%;max-width:44%}
  .ins-card-body{padding:14px 12px}
}
`;

/* ─── Reusable: course popularity + best performances ─────────── */

const CourseRank: React.FC<{ courses: CoursePop[] }> = ({ courses }) => {
  if (courses.length === 0) return <div className="ins-chart-empty">No rounds yet.</div>;
  const top = num(courses[0].rounds) || 1;
  return (
    <div className="ins-rank">
      {courses.map((c, i) => (
        <div key={i} className="ins-rank-row">
          <span className="ins-rank-name" title={c.facility_name ? `${c.course_name} · ${c.facility_name}` : c.course_name}>
            <span className="ins-rank-course">{c.course_name}</span>
            {c.facility_name && c.facility_name !== c.course_name && <span className="ins-rank-fac">{c.facility_name}</span>}
          </span>
          <span className="ins-rank-bar"><span className="ins-rank-fill" style={{ width: `${(num(c.rounds) / top) * 100}%` }} /></span>
          <span className="ins-rank-val">{num(c.rounds)}</span>
        </div>
      ))}
    </div>
  );
};

// Filterable lowest-gross leaderboard. Filters compose (AND) and the list is
// re-ranked by gross within the filtered set; a stepper expands 5 at a time.
const BestPerformances: React.FC<{ rows: BestPerf[]; showSeason?: boolean }> = ({ rows, showSeason }) => {
  const [limit, setLimit] = useState(5);
  const [player, setPlayer] = useState('');
  const [course, setCourse] = useState('');
  const [season, setSeason] = useState('');
  const [type, setType] = useState<'all' | 'regular' | 'major'>('all');

  // Distinct filter options, derived from the data.
  const players = useMemo(() => Array.from(new Set(rows.map(perfName))).sort(), [rows]);
  const courses = useMemo(() => Array.from(new Set(rows.map((r) => r.course_name))).sort(), [rows]);
  const seasons = useMemo(
    () => Array.from(new Set(rows.map((r) => r.season_year).filter((s): s is string => !!s))).sort().reverse(),
    [rows]
  );

  const filtered = useMemo(
    () =>
      rows.filter((r) => {
        if (player && perfName(r) !== player) return false;
        if (course && r.course_name !== course) return false;
        if (showSeason && season && r.season_year !== season) return false;
        if (type === 'regular' && r.is_major) return false;
        if (type === 'major' && !r.is_major) return false;
        return true;
      }),
    [rows, player, course, season, type, showSeason]
  );

  const anyFilter = !!player || !!course || (showSeason && !!season) || type !== 'all';
  const clearAll = () => { setPlayer(''); setCourse(''); setSeason(''); setType('all'); };

  if (rows.length === 0) return <div className="ins-chart-empty">No rounds yet.</div>;

  const visible = filtered.slice(0, limit);

  return (
    <>
      <div className="ins-filters">
        <select value={player} onChange={(e) => setPlayer(e.target.value)}>
          <option value="">All players</option>
          {players.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <select value={course} onChange={(e) => setCourse(e.target.value)}>
          <option value="">All courses</option>
          {courses.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        {showSeason && (
          <select value={season} onChange={(e) => setSeason(e.target.value)}>
            <option value="">All seasons</option>
            {seasons.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        )}
        <div className="ins-seg ins-seg-sm">
          <button className={type === 'all' ? 'active' : ''} onClick={() => setType('all')}>All</button>
          <button className={type === 'regular' ? 'active' : ''} onClick={() => setType('regular')}>Reg</button>
          <button className={type === 'major' ? 'active' : ''} onClick={() => setType('major')}>Major</button>
        </div>
        {anyFilter && <button className="ins-more-btn ghost" onClick={clearAll}>Clear</button>}
        <span className="ins-muted" style={{ marginLeft: 'auto' }}>{filtered.length} result{filtered.length === 1 ? '' : 's'}</span>
      </div>

      {filtered.length === 0 ? (
        <div className="ins-chart-empty">No performances match these filters.</div>
      ) : (
        <>
          <FitTable>
            <table className="tbl ins-tbl">
              <thead>
                <tr>
                  <th>#</th><th>Player</th><th className="r">Gross</th><th className="r">Net</th>
                  <th>Course</th><th>Date</th>{showSeason && <th>Season</th>}<th>Type</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((b, i) => (
                  <tr key={i}>
                    <td style={{ color: '#94a3b8', fontWeight: 700 }}>{i + 1}</td>
                    <td className="tbl-name" style={{ fontWeight: 700, color: '#1e293b' }}><PlayerName first={b.first_name} last={b.last_name} /></td>
                    <td className="r" style={{ fontWeight: 800 }}>{num(b.gross)}</td>
                    <td className="r">{num(b.net)}</td>
                    <td>{b.course_name}</td>
                    <td>{fmtDate(b.date)}</td>
                    {showSeason && <td>{b.season_year ?? '—'}</td>}
                    <td>
                      {b.is_major
                        ? <span className="ins-badge ins-badge-major">Major</span>
                        : <span className="ins-badge ins-badge-reg">Regular</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </FitTable>
          {(limit < filtered.length || limit > 5) && (
            <div className="ins-more">
              {limit < filtered.length && (
                <button className="ins-more-btn" onClick={() => setLimit((l) => Math.min(filtered.length, l + 5))}>
                  Show more
                </button>
              )}
              {limit < filtered.length && (
                <button className="ins-more-btn" onClick={() => setLimit(filtered.length)}>Show all ({filtered.length})</button>
              )}
              {limit > 5 && (
                <button className="ins-more-btn ghost" onClick={() => setLimit(5)}>Show less</button>
              )}
            </div>
          )}
        </>
      )}
    </>
  );
};

/* ─── League tab ──────────────────────────────────────────────── */

const LeagueTab: React.FC<{ data: LeagueInsights | null; scoreMode: ScoreMode; setScoreMode: (m: ScoreMode) => void }> = ({
  data,
  scoreMode,
  setScoreMode,
}) => {
  if (!data) return null;
  const trend = data.score_trend;

  const chartData: ChartPoint[] = trend.map((r) => {
    const v = scoreMode === 'net' ? num(r.avg_net) : num(r.avg_gross);
    return {
      label: fmtDate(r.date),
      value: v,
      tooltip: `${fmtDate(r.date)} · ${r.course_name}\nAvg ${scoreMode}: ${v}  ·  ${num(r.players)} players`,
    };
  });

  const partData = trend.map((r) => ({
    label: fmtDate(r.date),
    value: num(r.players),
    tooltip: `${fmtDate(r.date)} · ${r.course_name}: ${num(r.players)} players`,
  }));

  const low = data.lowest_net;
  const lowG = data.best_performances && data.best_performances.length ? data.best_performances[0] : null;

  return (
    <>
      <div className="ins-grid">
        <StatCard label="Rounds played" value={num(data.summary.total_rounds)} sub="regular rounds this season" />
        <StatCard label="Active players" value={num(data.summary.active_players)} />
        <StatCard label="Avg gross (18h)" value={data.summary.avg_gross ?? '—'} sub={`net ${data.summary.avg_net ?? '—'}`} />
        <StatCard
          label="Lowest gross (18h)"
          value={lowG ? num(lowG.gross) : '—'}
          accent={lowG ? '#16a34a' : undefined}
          sub={lowG ? `${lowG.first_name} ${lowG.last_name} · ${fmtDate(lowG.date)}` : 'No rounds yet'}
        />
      </div>

      <Card
        icon="📈"
        title="League scoring trend"
        right={<Toggle value={scoreMode} onChange={setScoreMode} />}
      >
        <LineChart data={chartData} color="#2563eb" lowerIsBetter />
        <div className="ins-legend">
          <span><i className="ins-key" style={{ background: '#2563eb' }} /> Field average ({scoreMode})</span>
          <span><i className="ins-key" style={{ background: '#16a34a' }} /> Best round of the season</span>
          <span>18-hole regular rounds, oldest → newest</span>
        </div>
      </Card>

      <Card icon="👥" title="Participation per round">
        <LineChart data={partData} color="#7c3aed" lowerIsBetter={false} />
        <div className="ins-legend">
          <span><i className="ins-key" style={{ background: '#7c3aed' }} /> Players in the field</span>
          <span><i className="ins-key" style={{ background: '#16a34a' }} /> Biggest field</span>
          <span>each 18-hole regular round · hover or tap a point</span>
        </div>
      </Card>

      <Card icon="⛳" title="Course popularity" right={<span className="ins-muted">rounds played this season</span>}>
        <CourseRank courses={data.courses} />
      </Card>

      <Card icon="🥇" title="Best performances" right={<span className="ins-muted">lowest 18-hole gross</span>}>
        <BestPerformances rows={data.best_performances} />
      </Card>
    </>
  );
};

/* ─── Players tab ─────────────────────────────────────────────── */

const PlayersTab: React.FC<{
  players: PlayerLite[];
  selectedId: number | null;
  onSelect: (id: number) => void;
  data: PlayerInsights | null;
  loading: boolean;
}> = ({ players, selectedId, onSelect, data, loading }) => {
  // Derive all summary stats from the timeline — one source of truth.
  const stats = useMemo(() => {
    if (!data) return null;
    const rounds = data.timeline.filter((r) => !r.is_disqualified);
    const eighteen = rounds.filter((r) => num(r.number_of_holes) === 18);
    const wins = rounds.filter((r) => num(r.rank) === 1).length;
    const podiums = rounds.filter((r) => num(r.rank) <= 3).length;
    const avgNet = eighteen.length ? eighteen.reduce((a, r) => a + num(r.net_score), 0) / eighteen.length : null;
    const avgGross = eighteen.length ? eighteen.reduce((a, r) => a + num(r.gross_score), 0) / eighteen.length : null;
    const best = eighteen.reduce<TimelineRound | null>(
      (b, r) => (b === null || num(r.net_score) < num(b.net_score) ? r : b),
      null
    );
    const bestGross = eighteen.reduce<TimelineRound | null>(
      (b, r) => (b === null || num(r.gross_score) < num(b.gross_score) ? r : b),
      null
    );
    return {
      played: data.timeline.length,
      wins,
      podiums,
      avgNet: avgNet === null ? null : Math.round(avgNet * 10) / 10,
      avgGross: avgGross === null ? null : Math.round(avgGross * 10) / 10,
      best,
      bestGross,
    };
  }, [data]);

  // Trend uses 18-hole rounds (comparable); net is the primary line and gross is
  // shown as a second line. Majors are marked on the net line.
  const chartRounds = useMemo(
    () => (data ? data.timeline.filter((r) => num(r.number_of_holes) === 18 && !r.is_disqualified) : []),
    [data]
  );
  const chartData: ChartPoint[] = useMemo(
    () =>
      chartRounds.map((r) => ({
        label: fmtDate(r.date),
        value: num(r.net_score),
        marked: r.is_major,
        tooltip:
          `${fmtDate(r.date)} · ${r.course_name}${r.is_major ? ` (${r.tournament_name || 'Major'})` : ''}\n` +
          `Net ${num(r.net_score)} · Gross ${num(r.gross_score)}  ·  finished ${medalFor(num(r.rank))} of ${num(r.field_size)}`,
      })),
    [chartRounds]
  );
  const grossSeries = useMemo(() => chartRounds.map((r) => num(r.gross_score)), [chartRounds]);

  const recentForm = useMemo(() => {
    if (!data) return [];
    return data.timeline
      .filter((r) => !r.is_disqualified)
      .slice(-5)
      .map((r) => num(r.rank));
  }, [data]);

  const hasMajor = data?.timeline.some((r) => r.is_major && num(r.number_of_holes) === 18);

  return (
    <>
      <div className="ins-picker">
        <label htmlFor="ins-player">Focus on player</label>
        <select
          id="ins-player"
          value={selectedId ?? ''}
          onChange={(e) => onSelect(Number(e.target.value))}
        >
          {players.map((p) => (
            <option key={p.player_id} value={p.player_id}>
              {p.first_name} {p.last_name}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="ins-loading">Loading player…</div>
      ) : !data || stats === null ? null : data.timeline.length === 0 ? (
        <Card icon="📭" title={`${data.player.first_name} ${data.player.last_name}`}>
          <div className="ins-chart-empty">No rounds recorded for this player in the selected season.</div>
        </Card>
      ) : (
        <>
          <div className="ins-grid">
            <StatCard label="Rounds played" value={stats.played} />
            <StatCard label="Avg gross (18h)" value={stats.avgGross ?? '—'} sub={`net ${stats.avgNet ?? '—'}`} />
            <StatCard
              label="Lowest gross (18h)"
              value={stats.bestGross ? num(stats.bestGross.gross_score) : '—'}
              accent={stats.bestGross ? '#16a34a' : undefined}
              sub={stats.bestGross ? `${stats.bestGross.course_name} · ${fmtDate(stats.bestGross.date)}` : undefined}
            />
            <StatCard label="Wins" value={stats.wins} sub={`${stats.podiums} podium${stats.podiums === 1 ? '' : 's'}`} accent={stats.wins > 0 ? '#b45309' : undefined} />
            <StatCard
              label="Season handicap"
              value={data.player.season_handicap ?? '—'}
              accent={data.player.season_handicap != null ? '#7c3aed' : undefined}
              sub={data.player.season_handicap != null
                ? (data.player.season_handicap_method === 'avg' ? 'avg of recorded · this season' : 'index · this season')
                : 'no handicap this season'}
            />
          </div>

          <Card icon="📈" title="Performance over time">
            <LineChart data={chartData} series2={grossSeries} color="#2563eb" color2="#94a3b8" lowerIsBetter />
            <div className="ins-legend">
              <span><i className="ins-key" style={{ background: '#2563eb' }} /> Net</span>
              <span><i className="ins-key" style={{ background: '#94a3b8' }} /> Gross</span>
              {hasMajor ? <span><i className="ins-key" style={{ background: '#f59e0b' }} /> Major</span> : null}
              <span>18-hole rounds · hover or tap a point</span>
            </div>
          </Card>

          <Card icon="🔥" title="Recent form" right={<span className="ins-muted">last 5 finishes</span>}>
            <FormStrip ranks={recentForm} />
          </Card>

          <Card icon="🗒️" title="Round log">
            <FitTable>
              <table className="tbl ins-tbl">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Course</th>
                    <th>Type</th>
                    <th className="r">Holes</th>
                    <th className="r">Gross</th>
                    <th className="r">Net</th>
                    <th className="r">Finish</th>
                  </tr>
                </thead>
                <tbody>
                  {[...data.timeline].reverse().map((r) => (
                    <tr key={r.round_id}>
                      <td>{fmtDate(r.date)}</td>
                      <td>{r.course_name}</td>
                      <td>
                        {r.is_major
                          ? <span className="ins-badge ins-badge-major" title={r.tournament_name || 'Major'}>Major</span>
                          : <span className="ins-badge ins-badge-reg">Regular</span>}
                      </td>
                      <td className="r">{num(r.number_of_holes)}</td>
                      <td className="r">{r.is_disqualified ? 'DQ' : num(r.gross_score)}</td>
                      <td className="r">{r.is_disqualified ? '—' : num(r.net_score)}</td>
                      <td className="r">{r.is_disqualified ? '—' : `${medalFor(num(r.rank))} / ${num(r.field_size)}`}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </FitTable>
          </Card>
        </>
      )}
    </>
  );
};

/* ─── League · year over year ─────────────────────────────────── */

const LeagueYoYTab: React.FC<{ data: LeagueYoY | null; scoreMode: ScoreMode; setScoreMode: (m: ScoreMode) => void; handicapsBySeason: HcpSeasonRow[] | null }> = ({
  data,
  scoreMode,
  setScoreMode,
  handicapsBySeason,
}) => {
  // Participation card can show regular-season players or major participants.
  const [partMode, setPartMode] = useState<'regular' | 'major'>('regular');

  // Pivot every member's per-season handicap index into one series per player,
  // aligned to a shared season axis, for the overlay chart.
  const hcpChart = useMemo(() => {
    const rows = handicapsBySeason || [];
    if (rows.length === 0) return null;
    const years = Array.from(new Set(rows.map((r) => r.season_year))).sort();
    const byPlayer = new Map<number, { name: string; map: Map<string, number> }>();
    for (const r of rows) {
      if (!byPlayer.has(r.player_id)) byPlayer.set(r.player_id, { name: `${r.first_name} ${r.last_name}`, map: new Map() });
      byPlayer.get(r.player_id)!.map.set(r.season_year, Number(r.handicap));
    }
    const series: MultiSeries[] = Array.from(byPlayer.values())
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((p) => ({ name: p.name, values: years.map((y) => (p.map.has(y) ? (p.map.get(y) as number) : null)) }));
    return { years, series };
  }, [handicapsBySeason]);

  if (!data) return null;
  const seasons = data.seasons;
  if (seasons.length === 0) {
    return <Card icon="📊" title="Year over year"><div className="ins-chart-empty">No season data yet.</div></Card>;
  }

  // Scoring trend — skip seasons with no 18-hole regular scoring.
  const scored = seasons.filter((d) => (scoreMode === 'net' ? d.avg_net : d.avg_gross) != null);
  const chartData: ChartPoint[] = scored.map((d) => {
    const v = num(scoreMode === 'net' ? d.avg_net : d.avg_gross);
    return {
      label: d.season_year,
      value: v,
      tooltip: `${d.season_year}\nAvg ${scoreMode}: ${v}  ·  ${num(d.active_players)} players`,
    };
  });

  // Rounds per season, broken out regular vs major.
  const roundsData = seasons.map((d) => ({
    label: d.season_year,
    reg: num(d.reg_rounds),
    major: num(d.major_rounds),
    tooltip: `${d.season_year}: ${num(d.total_rounds)} total · ${num(d.reg_rounds)} regular · ${num(d.major_rounds)} major`,
  }));

  const partData = seasons.map((d) => {
    const v = partMode === 'regular' ? num(d.active_players) : num(d.major_participants);
    return {
      label: d.season_year,
      value: v,
      tooltip: `${d.season_year}: ${v} ${partMode === 'regular' ? 'regular-season players' : 'major participants'}`,
    };
  });

  const totalReg = seasons.reduce((a, d) => a + num(d.reg_rounds), 0);
  const totalMajor = seasons.reduce((a, d) => a + num(d.major_rounds), 0);
  // Best scoring season is the lowest average GROSS.
  const grossScored = seasons.filter((d) => d.avg_gross != null);
  const bestSeason = grossScored.reduce<LeagueSeasonAgg | null>(
    (b, d) => (b === null || num(d.avg_gross) < num(b.avg_gross) ? d : b),
    null
  );
  const big = data.biggest_round;

  // Man-games: dynamic columns for every group size that occurs.
  const sizes = Array.from(new Set(seasons.flatMap((s) => s.man_games.map((m) => m.size)))).sort((a, b) => a - b);
  const mg = (s: LeagueSeasonAgg, size: number) => s.man_games.find((m) => m.size === size)?.rounds ?? 0;

  return (
    <>
      <div className="ins-grid">
        <StatCard label="Seasons tracked" value={seasons.length} />
        <StatCard label="Total rounds" value={totalReg + totalMajor} sub={`${totalReg} reg · ${totalMajor} major`} />
        <StatCard
          label="Best scoring season"
          value={bestSeason ? num(bestSeason.avg_gross) : '—'}
          accent={bestSeason ? '#16a34a' : undefined}
          sub={bestSeason ? `${bestSeason.season_year} · avg gross` : undefined}
        />
        <StatCard
          label="Most players in a round"
          value={big ? num(big.players) : '—'}
          sub={big ? `${fmtDate(big.date)} ${big.season_year} · ${big.course_name}${big.is_major ? ' (Major)' : ''}` : undefined}
        />
      </div>

      <Card icon="📈" title="League scoring · year over year" right={<Toggle value={scoreMode} onChange={setScoreMode} />}>
        <LineChart data={chartData} color="#2563eb" lowerIsBetter />
        <div className="ins-legend">
          <span><i className="ins-key" style={{ background: '#2563eb' }} /> Field average ({scoreMode})</span>
          <span><i className="ins-key" style={{ background: '#16a34a' }} /> Best season</span>
          <span>18-hole regular rounds · hover or tap a point for the value</span>
        </div>
      </Card>

      {hcpChart && (
        <Card icon="📏" title="Handicap index · everyone, year over year" right={<span className="ins-muted">lower is better</span>}>
          <MultiLineChart xLabels={hcpChart.years} series={hcpChart.series} lowerIsBetter />
          <p className="ins-muted" style={{ fontSize: 12.5, lineHeight: 1.5, margin: '10px 2px 0' }}>
            Each line is a member's handicap <strong>index for that season</strong> (computed from that year's
            18-hole rounds). It compares how everyone performed year to year — it is not the official playing
            handicap, which is based on each player's most recent 20 rounds. Hover a year to rank everyone.
          </p>
        </Card>
      )}

      <Card icon="🗓️" title="Rounds played per season" right={
        <div className="ins-legend" style={{ margin: 0 }}>
          <span><i className="ins-key" style={{ background: '#2563eb' }} /> Regular</span>
          <span><i className="ins-key" style={{ background: '#f59e0b' }} /> Major</span>
        </div>
      }>
        <GroupedBars data={roundsData} />
      </Card>

      <Card
        icon="👥"
        title="Participation by season"
        right={
          <div className="ins-seg ins-seg-sm">
            <button className={partMode === 'regular' ? 'active' : ''} onClick={() => setPartMode('regular')}>Regular</button>
            <button className={partMode === 'major' ? 'active' : ''} onClick={() => setPartMode('major')}>Major</button>
          </div>
        }
      >
        <Bars data={partData} />
      </Card>

      <Card icon="👬" title="Man-games by season" right={<span className="ins-muted">regular rounds by group size</span>}>
        <FitTable>
          <table className="tbl ins-tbl">
            <thead>
              <tr>
                <th>Season</th>
                {sizes.map((sz) => <th key={sz} className="r">{sz}-man</th>)}
                <th className="r">Total</th>
                <th className="r" title="Average group size — players per round, across that season's regular rounds">Avg</th>
              </tr>
            </thead>
            <tbody>
              {[...seasons].reverse().map((d) => {
                const totalRounds = d.man_games.reduce((a, m) => a + num(m.rounds), 0);
                const totalPlayers = d.man_games.reduce((a, m) => a + num(m.size) * num(m.rounds), 0);
                const avg = totalRounds ? totalPlayers / totalRounds : 0;
                return (
                  <tr key={d.season_year}>
                    <td style={{ fontWeight: 700, color: '#1e293b' }}>{d.season_year}</td>
                    {sizes.map((sz) => {
                      const c = mg(d, sz);
                      return <td key={sz} className="r">{c > 0 ? c : <span className="ins-muted">·</span>}</td>;
                    })}
                    <td className="r" style={{ fontWeight: 700 }}>{totalRounds}</td>
                    <td className="r" style={{ fontWeight: 700, color: '#2563eb' }}>{avg ? avg.toFixed(1) : <span className="ins-muted">·</span>}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              {(() => {
                const tR = seasons.reduce((a, d) => a + d.man_games.reduce((x, m) => x + num(m.rounds), 0), 0);
                const tP = seasons.reduce((a, d) => a + d.man_games.reduce((x, m) => x + num(m.size) * num(m.rounds), 0), 0);
                const overall = tR ? tP / tR : 0;
                return (
                  <tr>
                    <td style={{ fontWeight: 700, color: '#475569' }}>All seasons</td>
                    {sizes.map((sz) => {
                      const c = seasons.reduce((a, d) => a + (d.man_games.find((m) => m.size === sz)?.rounds ?? 0), 0);
                      return <td key={sz} className="r" style={{ color: '#475569' }}>{c > 0 ? c : <span className="ins-muted">·</span>}</td>;
                    })}
                    <td className="r" style={{ fontWeight: 700 }}>{tR}</td>
                    <td className="r" style={{ fontWeight: 800, color: '#2563eb' }}>{overall ? overall.toFixed(1) : '·'}</td>
                  </tr>
                );
              })()}
            </tfoot>
          </table>
        </FitTable>
      </Card>

      <Card icon="🏆" title="Majors by season">
        <FitTable>
          <table className="tbl ins-tbl">
            <thead>
              <tr>
                <th>Season</th>
                <th className="r">Majors</th>
                <th className="r">Major Rounds</th>
                <th className="r">Participants</th>
              </tr>
            </thead>
            <tbody>
              {[...seasons].reverse().map((d) => (
                <tr key={d.season_year}>
                  <td style={{ fontWeight: 700, color: '#1e293b' }}>{d.season_year}</td>
                  <td className="r">{num(d.majors)}</td>
                  <td className="r">{num(d.major_rounds)}</td>
                  <td className="r">{num(d.major_participants)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </FitTable>
      </Card>

      <Card icon="🗂️" title="Season comparison" right={<span className="ins-muted">scoring from 18-hole regular rounds</span>}>
        <FitTable>
          <table className="tbl ins-tbl">
            <thead>
              <tr>
                <th>Season</th>
                <th className="r">Reg</th>
                <th className="r">Major</th>
                <th className="r">Players</th>
                <th className="r">Avg Gross</th>
                <th className="r">Avg Net</th>
                <th className="r">Low Gross</th>
                <th className="r">Low Net</th>
              </tr>
            </thead>
            <tbody>
              {[...seasons].reverse().map((d) => (
                <tr key={d.season_year}>
                  <td style={{ fontWeight: 700, color: '#1e293b' }}>{d.season_year}</td>
                  <td className="r">{num(d.reg_rounds)}</td>
                  <td className="r">{num(d.major_rounds)}</td>
                  <td className="r">{num(d.active_players)}</td>
                  <td className="r">{d.avg_gross ?? '—'}</td>
                  <td className="r">{d.avg_net ?? '—'}</td>
                  <td className="r">{d.low_gross ?? '—'}</td>
                  <td className="r">{d.low_net ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </FitTable>
      </Card>

      <Card icon="⛳" title="Course popularity · all time" right={<span className="ins-muted">rounds played, every season</span>}>
        <CourseRank courses={data.courses} />
      </Card>

      <Card icon="🥇" title="Best performances · all time" right={<span className="ins-muted">lowest 18-hole gross</span>}>
        <BestPerformances rows={data.best_performances} showSeason />
      </Card>
    </>
  );
};

/* ─── Players · year over year ────────────────────────────────── */

/* ─── Trophy case (player · all seasons) ──────────────────────── */

// Distinct, hand-drawn silhouettes so each title reads at a glance:
// North = the Open's claret jug, West = a classic silver cup, East = a golfer
// statuette, League = a championship belt. Tremblant majors get the Tremblant
// resort logo (red banner + peak wordmark).
const ClaretJug: React.FC = () => (
  <svg width="38" height="54" viewBox="0 0 38 54" aria-hidden>
    <defs>
      <linearGradient id="tcSilver" x1="0" y1="0" x2="0" y2="1">
        <stop stopColor="#f8fafc" /><stop offset=".5" stopColor="#cbd5e1" /><stop offset="1" stopColor="#94a3b8" />
      </linearGradient>
    </defs>
    <rect x="11" y="49" width="16" height="4" rx="1" fill="#94a3b8" />
    <rect x="15" y="42" width="8" height="8" fill="url(#tcSilver)" />
    <path d="M9 28c0 11 20 11 20 0l-2 14c-5 4-11 4-16 0z" fill="url(#tcSilver)" stroke="#94a3b8" strokeWidth=".5" />
    <path d="M9 28c2-8 18-8 20 0z" fill="url(#tcSilver)" />
    <path d="M9 24c-5 1-4 8 1 9" fill="none" stroke="#cbd5e1" strokeWidth="2.2" />
    <path d="M29 24c5 1 4 8-1 9" fill="none" stroke="#cbd5e1" strokeWidth="2.2" />
    <rect x="16" y="15" width="6" height="9" fill="url(#tcSilver)" />
    <path d="M14 15c2-5 8-5 10 0z" fill="url(#tcSilver)" />
    <circle cx="19" cy="8" r="2.6" fill="url(#tcSilver)" /><rect x="18" y="10" width="2" height="5" fill="#cbd5e1" />
  </svg>
);
const SilverCup: React.FC = () => (
  <svg width="38" height="54" viewBox="0 0 38 54" aria-hidden>
    <rect x="11" y="49" width="16" height="4" rx="1" fill="#94a3b8" />
    <rect x="16" y="40" width="6" height="9" fill="url(#tcSilver)" />
    <path d="M9 14h20v8c0 9-20 9-20 0z" fill="url(#tcSilver)" stroke="#94a3b8" strokeWidth=".5" />
    <path d="M9 16c-6 0-6 12 2 12" fill="none" stroke="#cbd5e1" strokeWidth="2.4" />
    <path d="M29 16c6 0 6 12-2 12" fill="none" stroke="#cbd5e1" strokeWidth="2.4" />
    <rect x="8" y="11" width="22" height="4" rx="1.5" fill="#cbd5e1" />
  </svg>
);
const GolferStatue: React.FC = () => (
  <svg width="38" height="54" viewBox="0 0 38 54" aria-hidden>
    <defs>
      <linearGradient id="tcGold" x1="0" y1="0" x2="0" y2="1">
        <stop stopColor="#fde68a" /><stop offset=".5" stopColor="#d4af37" /><stop offset="1" stopColor="#a16207" />
      </linearGradient>
    </defs>
    <rect x="9" y="46" width="20" height="6" rx="1.5" fill="#a16207" />
    <rect x="12" y="40" width="14" height="6" fill="url(#tcGold)" />
    <circle cx="19" cy="11" r="3.4" fill="url(#tcGold)" />
    <path d="M19 15c-3 0-4 3-4 6l-3 8 3 1 4-8 4 8 3-1-3-8c0-3-1-6-4-6z" fill="url(#tcGold)" />
    <path d="M22 18l8-7" stroke="url(#tcGold)" strokeWidth="2.2" strokeLinecap="round" />
  </svg>
);
const ChampionBelt: React.FC = () => (
  <svg width="48" height="54" viewBox="0 0 48 54" aria-hidden>
    <defs>
      <linearGradient id="tcBeltGold" x1="0" y1="0" x2="0" y2="1">
        <stop stopColor="#fde68a" /><stop offset="1" stopColor="#b8860b" />
      </linearGradient>
    </defs>
    <rect x="2" y="22" width="44" height="11" rx="3" fill="#166534" />
    <rect x="2" y="22" width="44" height="11" rx="3" fill="none" stroke="#14532d" strokeWidth="1" />
    <rect x="6" y="20" width="9" height="15" rx="2" fill="url(#tcBeltGold)" stroke="#92400e" strokeWidth=".6" />
    <rect x="33" y="20" width="9" height="15" rx="2" fill="url(#tcBeltGold)" stroke="#92400e" strokeWidth=".6" />
    <ellipse cx="24" cy="27.5" rx="12" ry="13" fill="url(#tcBeltGold)" stroke="#92400e" strokeWidth="1" />
    <path d="M24 21l1.8 3.7 4 .6-2.9 2.8.7 4-3.6-1.9-3.6 1.9.7-4-2.9-2.8 4-.6z" fill="#166534" />
  </svg>
);
const Medal: React.FC = () => (
  <svg width="38" height="54" viewBox="0 0 38 54" aria-hidden>
    <path d="M13 6l6 18 6-18" fill="none" stroke="#b45309" strokeWidth="4" />
    <circle cx="19" cy="34" r="13" fill="url(#tcGold)" stroke="#a16207" strokeWidth="1" />
    <path d="M19 27l1.9 3.9 4.3.6-3.1 3 .7 4.3-3.8-2-3.8 2 .7-4.3-3.1-3 4.3-.6z" fill="#a16207" />
  </svg>
);

// Tremblant resort wordmark: red rounded banner with the mountain peak above
// and the "TREMBLANT" wordmark — instantly recognisable vs the metal trophies.
const TremblantLogo: React.FC = () => (
  <svg width="58" height="46" viewBox="0 0 58 46" aria-hidden>
    <defs>
      <linearGradient id="tcTremRed" x1="0" y1="0" x2="0" y2="1">
        <stop stopColor="#e2231a" /><stop offset="1" stopColor="#b71c12" />
      </linearGradient>
    </defs>
    {/* mountain peak above the banner */}
    <path d="M21 22L29 7l8 15z" fill="url(#tcTremRed)" />
    <path d="M26.4 22L29 17l2.6 5z" fill="#fff" opacity=".92" />
    {/* red banner */}
    <rect x="1" y="20" width="56" height="20" rx="10" fill="url(#tcTremRed)" stroke="#8f1109" strokeWidth=".6" />
    {/* wordmark */}
    <text x="29" y="33.6" textAnchor="middle" fontFamily="Arial, Helvetica, sans-serif"
      fontWeight="700" fontSize="8" letterSpacing=".3" fill="#fff">TREMBLANT</text>
  </svg>
);

// Renders the real Tremblant logo image (public/tremblant-logo.svg). Falls back
// to the drawn wordmark above if the file isn't present, so the build/app never
// breaks on a missing asset.
const TremblantBadge: React.FC = () => {
  const [failed, setFailed] = React.useState(false);
  if (failed) return <TremblantLogo />;
  return (
    <img
      src={`${process.env.PUBLIC_URL || ''}/tremblant-logo.svg`}
      alt="Tremblant"
      style={{ height: 46, width: 'auto', maxWidth: 72, objectFit: 'contain' }}
      onError={() => setFailed(true)}
    />
  );
};

const TROPHY_RENDER: Record<string, React.FC> = {
  North: ClaretJug, West: SilverCup, East: GolferStatue, Tremblant: TremblantBadge, League: ChampionBelt,
};

// One mini-card per title, stamped with the year it was won.
type TrophyWin = { kind: string; label: string; year: number };

const REGION_LABEL: Record<string, string> = {
  North: 'North Major', West: 'West Major', East: 'East Major', Tremblant: 'Tremblant',
};

const TrophyTile: React.FC<{ kind: string; label: string; year: number }> = ({ kind, label, year }) => {
  const Icon = TROPHY_RENDER[kind] || Medal;
  return (
    <div style={{
      position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
      width: 104, padding: '14px 8px 12px', borderRadius: 10,
      background: 'linear-gradient(180deg,#1e293b 0%,#0f172a 100%)',
      boxShadow: 'inset 0 -3px 0 rgba(0,0,0,0.4), 0 2px 6px rgba(0,0,0,0.15)',
    }}>
      <div style={{ height: 54, display: 'flex', alignItems: 'flex-end' }}><Icon /></div>
      <span style={{ fontSize: 11, fontWeight: 700, color: '#e2e8f0', textAlign: 'center', lineHeight: 1.2 }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 800, color: '#fbbf24', letterSpacing: '0.02em' }}>{year}</span>
    </div>
  );
};

// Build a flat, chronologically-ordered list of a player's titles (one entry
// per win) from their major-title rows and league championship years.
const buildTrophyWins = (majorRows: MajorTitleRow[], leagueYears: (string | number)[]): TrophyWin[] => {
  const wins: TrophyWin[] = [];
  for (const r of majorRows) {
    const label = REGION_LABEL[r.region] || r.region;
    for (const y of r.years || []) wins.push({ kind: r.region, label, year: Number(y) });
  }
  for (const y of leagueYears) wins.push({ kind: 'League', label: 'League Championship', year: Number(y) });
  return wins.sort((a, b) => a.year - b.year);
};

const TrophyCase: React.FC<{ wins: TrophyWin[] }> = ({ wins }) => {
  if (wins.length === 0) return null;
  const majors = wins.filter((w) => w.kind !== 'League').length;
  const league = wins.length - majors;
  return (
    <Card icon="🏆" title="Trophy case" right={<span className="ins-muted">{majors} major{majors === 1 ? '' : 's'}{league ? ` · ${league} league` : ''}</span>}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, padding: '4px 0' }}>
        {wins.map((w, i) => <TrophyTile key={`${w.kind}-${w.year}-${i}`} kind={w.kind} label={w.label} year={w.year} />)}
      </div>
    </Card>
  );
};

const PlayersYoYTab: React.FC<{
  players: PlayerLite[];
  selectedId: number | null;
  onSelect: (id: number) => void;
  data: PlayerSeasonAgg[] | null;
  loading: boolean;
  majorTitles: MajorTitles | null;
  leagueTitles: LeagueTitleRow[] | null;
  currentIndex?: number;
}> = ({ players, selectedId, onSelect, data, loading, majorTitles, leagueTitles, currentIndex }) => {
  // Net is the primary line; gross is shown as a second line.
  const scored = (data || []).filter((d) => d.avg_net != null && d.avg_gross != null);
  const chartData: ChartPoint[] = scored.map((d) => ({
    label: d.season_year,
    value: num(d.avg_net),
    tooltip: `${d.season_year}\nNet ${num(d.avg_net)} · Gross ${num(d.avg_gross)}  ·  ${num(d.rounds_played)} rounds · ${num(d.wins)} wins`,
  }));
  const grossSeries = scored.map((d) => num(d.avg_gross));
  // Rounds per season — separate regular & major bars, with the total on top.
  const roundsData = (data || []).map((d) => ({
    label: d.season_year,
    reg: num(d.reg_rounds),
    major: num(d.major_rounds),
    tooltip: `${d.season_year}: ${num(d.rounds_played)} total · ${num(d.reg_rounds)} regular · ${num(d.major_rounds)} major`,
  }));

  const careerRounds = (data || []).reduce((a, d) => a + num(d.rounds_played), 0);
  const careerWins = (data || []).reduce((a, d) => a + num(d.wins), 0);
  const careerPodiums = (data || []).reduce((a, d) => a + num(d.podiums), 0);
  const bestSeason = scored.reduce<PlayerSeasonAgg | null>((b, d) => (b === null || num(d.avg_net) < num(b.avg_net) ? d : b), null);

  // Trophy case — this player's titles, one mini-card per win with its year.
  const playerMajorRows = (majorTitles && selectedId != null)
    ? majorTitles.rows.filter((r) => r.player_id === selectedId)
    : [];
  const playerLeagueYears = (leagueTitles && selectedId != null)
    ? (leagueTitles.find((r) => r.player_id === selectedId)?.years ?? [])
    : [];
  const trophyWins = buildTrophyWins(playerMajorRows, playerLeagueYears);

  return (
    <>
      <div className="ins-picker">
        <label htmlFor="ins-player-yoy">Focus on player</label>
        <select id="ins-player-yoy" value={selectedId ?? ''} onChange={(e) => onSelect(Number(e.target.value))}>
          {players.map((p) => (
            <option key={p.player_id} value={p.player_id}>{p.first_name} {p.last_name}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="ins-loading">Loading player…</div>
      ) : !data ? null : data.length === 0 ? (
        <Card icon="📭" title="Year over year">
          <div className="ins-chart-empty">No rounds recorded for this player yet.</div>
        </Card>
      ) : (
        <>
          <div className="ins-grid">
            <StatCard label="Seasons played" value={data.length} />
            <StatCard label="Career rounds" value={careerRounds} />
            <StatCard
              label="Career wins"
              value={careerWins}
              sub={`${careerPodiums} podium${careerPodiums === 1 ? '' : 's'}`}
              accent={careerWins > 0 ? '#b45309' : undefined}
            />
            <StatCard
              label="Best season"
              value={bestSeason ? num(bestSeason.avg_net) : '—'}
              accent={bestSeason ? '#16a34a' : undefined}
              sub={bestSeason ? `${bestSeason.season_year} · avg net` : undefined}
            />
            <StatCard
              label="Current handicap index"
              value={currentIndex != null ? currentIndex : '—'}
              accent={currentIndex != null ? '#7c3aed' : undefined}
              sub="as of today · last 20 rounds"
            />
          </div>

          <TrophyCase wins={trophyWins} />

          <Card icon="📈" title="Scoring · year over year">
            <LineChart data={chartData} series2={grossSeries} color="#2563eb" color2="#94a3b8" lowerIsBetter />
            <div className="ins-legend">
              <span><i className="ins-key" style={{ background: '#2563eb' }} /> Avg net (18h)</span>
              <span><i className="ins-key" style={{ background: '#94a3b8' }} /> Avg gross (18h)</span>
              <span>one point per season, oldest → newest · hover or tap a point</span>
            </div>
          </Card>

          <Card icon="🎯" title="Rounds by season" right={
            <div className="ins-legend" style={{ margin: 0 }}>
              <span><i className="ins-key" style={{ background: '#2563eb' }} /> Regular</span>
              <span><i className="ins-key" style={{ background: '#f59e0b' }} /> Major</span>
            </div>
          }>
            <GroupedBars data={roundsData} />
          </Card>

          {(() => {
            const hcpSeasons = (data || []).filter((d) => d.handicap != null);
            if (hcpSeasons.length === 0) return null;
            const hcpData: ChartPoint[] = hcpSeasons.map((d) => ({
              label: d.season_year,
              value: num(d.handicap),
              tooltip: `${d.season_year}\nHandicap ${num(d.handicap)} · ${d.handicap_method === 'avg' ? 'average of recorded handicaps' : 'index from that season’s rounds'}`,
            }));
            const omitted = (data || []).length - hcpSeasons.length;
            return (
              <Card icon="📏" title="Handicap per season" right={<span className="ins-muted">all time</span>}>
                <LineChart data={hcpData} color="#7c3aed" lowerIsBetter />
                <div className="ins-legend">
                  <span><i className="ins-key" style={{ background: '#7c3aed' }} /> Season handicap (lower is better)</span>
                </div>
                <p className="ins-muted" style={{ fontSize: 12.5, lineHeight: 1.5, margin: '10px 2px 0' }}>
                  This is a measure of how you <strong>performed that season</strong> — not your official playing
                  handicap. Your actual playing handicap is based on your most recent <strong>20 rounds</strong>,
                  a window that can span several seasons.
                  {omitted > 0 && ' Seasons with no handicap on record are left out.'}
                </p>
              </Card>
            );
          })()}

          <Card icon="🗂️" title="Season-by-season breakdown">
            <FitTable>
              <table className="tbl ins-tbl">
                <thead>
                  <tr>
                    <th>Season</th>
                    <th className="r" title="Regular-season rounds">Reg</th>
                    <th className="r" title="Major rounds">Maj</th>
                    <th className="r">Total</th>
                    <th className="r">Avg Gross</th>
                    <th className="r">Avg Net</th>
                    <th className="r">Best Gross</th>
                    <th className="r">Best Net</th>
                    <th className="r" title="Regular-season round wins">Reg W</th>
                    <th className="r" title="Major championships won">Maj W</th>
                    <th className="r">Podiums</th>
                  </tr>
                </thead>
                <tbody>
                  {[...data].reverse().map((d) => (
                    <tr key={d.season_year}>
                      <td style={{ fontWeight: 700, color: '#1e293b' }}>{d.season_year}</td>
                      <td className="r">{num(d.reg_rounds)}</td>
                      <td className="r">{num(d.major_rounds)}</td>
                      <td className="r" style={{ fontWeight: 700 }}>{num(d.rounds_played)}</td>
                      <td className="r">{d.avg_gross ?? '—'}</td>
                      <td className="r">{d.avg_net ?? '—'}</td>
                      <td className="r">{d.best_gross ?? '—'}</td>
                      <td className="r">{d.best_net ?? '—'}</td>
                      <td className="r">{num(d.reg_wins)}</td>
                      <td className="r" style={{ fontWeight: num(d.major_wins) > 0 ? 700 : 400, color: num(d.major_wins) > 0 ? '#b45309' : undefined }}>{num(d.major_wins)}</td>
                      <td className="r">{num(d.podiums)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </FitTable>
          </Card>
        </>
      )}
    </>
  );
};

/* ─── Titles tab ──────────────────────────────────────────────── */

const TitlesTab: React.FC<{
  leagueTitles: LeagueTitleRow[] | null;
  majorTitles: MajorTitles | null;
  loading: boolean;
}> = ({ leagueTitles, majorTitles, loading }) => {
  if (loading || !leagueTitles || !majorTitles) {
    return <div className="ins-loading">Loading titles…</div>;
  }

  // Pivot major wins: player → wins per region (+ total).
  const regions = majorTitles.regions;
  const byPlayer = new Map<number, { player_id: number; name: string; wins: Record<string, number>; total: number }>();
  for (const row of majorTitles.rows) {
    const cur = byPlayer.get(row.player_id) || {
      player_id: row.player_id,
      name: `${row.first_name} ${row.last_name}`,
      wins: {},
      total: 0,
    };
    cur.wins[row.region] = (cur.wins[row.region] || 0) + num(row.wins);
    cur.total += num(row.wins);
    byPlayer.set(row.player_id, cur);
  }
  const majorRows = Array.from(byPlayer.values()).sort((a, b) => b.total - a.total || a.name.localeCompare(b.name));

  const totalLeagueTitles = leagueTitles.reduce((a, r) => a + r.titles, 0);
  const totalMajorWins = majorRows.reduce((a, r) => a + r.total, 0);

  return (
    <>
      <div className="ins-grid">
        <StatCard label="League titles" value={totalLeagueTitles} sub="completed seasons" accent={totalLeagueTitles > 0 ? '#b45309' : undefined} />
        <StatCard label="Champions" value={leagueTitles.length} sub="players with ≥1 title" />
        <StatCard label="Major wins" value={totalMajorWins} sub={`${regions.length} region${regions.length === 1 ? '' : 's'}`} />
        <StatCard label="Major winners" value={majorRows.length} sub="players with ≥1 major" />
      </div>

      <Card icon="👑" title="League titles" right={<span className="ins-muted">season points champions · completed seasons</span>}>
        {leagueTitles.length === 0 ? (
          <div className="ins-chart-empty">No completed seasons yet.</div>
        ) : (
          <FitTable>
            <table className="tbl ins-tbl">
              <thead>
                <tr><th>Player</th><th className="r">Titles</th><th>Seasons won</th></tr>
              </thead>
              <tbody>
                {leagueTitles.map((r) => (
                  <tr key={r.player_id}>
                    <td className="tbl-name" style={{ fontWeight: 700, color: '#1e293b' }}><PlayerName first={r.first_name} last={r.last_name} /></td>
                    <td className="r" style={{ fontWeight: 800 }}>{r.titles}</td>
                    <td>{r.years.join(', ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </FitTable>
        )}
      </Card>

      <Card icon="🏆" title="Major titles by region" right={<span className="ins-muted">1st-place finishes in closed majors</span>}>
        {majorRows.length === 0 ? (
          <div className="ins-chart-empty">No regional major wins yet. Tag majors with a region on the Majors page.</div>
        ) : (
          <FitTable>
            <table className="tbl ins-tbl">
              <thead>
                <tr>
                  <th>Player</th>
                  {regions.map((rg) => <th key={rg} className="r">{rg}</th>)}
                  <th className="r">Total</th>
                </tr>
              </thead>
              <tbody>
                {majorRows.map((r) => (
                  <tr key={r.player_id}>
                    <td className="tbl-name" style={{ fontWeight: 700, color: '#1e293b' }}><PlayerName full={r.name} /></td>
                    {regions.map((rg) => {
                      const w = r.wins[rg] || 0;
                      return <td key={rg} className="r">{w > 0 ? w : <span className="ins-muted">·</span>}</td>;
                    })}
                    <td className="r" style={{ fontWeight: 800 }}>{r.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </FitTable>
        )}
      </Card>
    </>
  );
};

/* ─── Page ────────────────────────────────────────────────────── */

const Insights: React.FC = () => {
  const { getAccessTokenSilently, isAuthenticated, isLoading, loginWithRedirect, user } = useAuth0();
  const APIURL = process.env.REACT_APP_API_URL;

  const [seasons, setSeasons] = useState<Season[]>([]);
  // Defaults to year-over-year mode; pick a specific year to drill into rounds.
  const [selectedSeason, setSelectedSeason] = useState<string>(ALL_SEASONS);
  const [tab, setTab] = useState<Tab>('league');
  const [scoreMode, setScoreMode] = useState<ScoreMode>('gross');

  const [players, setPlayers] = useState<PlayerLite[]>([]);
  const [selectedPlayerId, setSelectedPlayerId] = useState<number | null>(null);

  const [league, setLeague] = useState<LeagueInsights | null>(null);
  const [playerData, setPlayerData] = useState<PlayerInsights | null>(null);
  const [leagueBySeason, setLeagueBySeason] = useState<LeagueYoY | null>(null);
  const [playerBySeason, setPlayerBySeason] = useState<PlayerSeasonAgg[] | null>(null);
  const [playerLoading, setPlayerLoading] = useState(false);

  const [majorTitles, setMajorTitles] = useState<MajorTitles | null>(null);
  const [leagueTitles, setLeagueTitles] = useState<LeagueTitleRow[] | null>(null);
  const [titlesLoading, setTitlesLoading] = useState(false);

  // Live handicap index (last 20 rounds) per player, and every player's
  // per-season index for the league overlay chart.
  const [liveIndex, setLiveIndex] = useState<Record<number, number>>({});
  const [hcpBySeason, setHcpBySeason] = useState<HcpSeasonRow[] | null>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const authHeader = async () => ({ Authorization: `Bearer ${await getAccessTokenSilently()}` });

  // Seasons + players (the player picker is shared, so fetch once up front).
  useEffect(() => {
    if (!isAuthenticated) {
      if (!isLoading) loginWithRedirect();
      return;
    }
    (async () => {
      try {
        const headers = await authHeader();
        const [seasonRes, playerRes, idxRes] = await Promise.all([
          axios.get<Season[]>(`${APIURL}/seasons`, { headers }),
          axios.get<PlayerLite[]>(`${APIURL}/players`, { headers }),
          axios.get<{ player_id: number; handicap_index: number | string }[]>(`${APIURL}/handicap-index`, { headers }),
        ]);
        setSeasons(seasonRes.data);
        const idxMap: Record<number, number> = {};
        for (const r of idxRes.data || []) idxMap[r.player_id] = Number(r.handicap_index);
        setLiveIndex(idxMap);

        const active = playerRes.data
          .filter((p) => p.league_status !== 'Removed')
          .sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`));
        setPlayers(active);
        // Default the focus to the signed-in member, else the first player.
        const me = active.find((p) => p.user_id && p.user_id === user?.sub);
        setSelectedPlayerId(me?.player_id ?? active[0]?.player_id ?? null);
      } catch {
        setError('Failed to load insights.');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isLoading]);

  // League insights — refetch on season change. "All seasons" loads the
  // year-over-year aggregate; a specific year loads the round-by-round detail.
  useEffect(() => {
    if (!selectedSeason) return;
    (async () => {
      try {
        const headers = await authHeader();
        if (selectedSeason === ALL_SEASONS) {
          const res = await axios.get<LeagueYoY>(`${APIURL}/insights/league/by-season`, { headers });
          setLeagueBySeason(res.data);
          if (hcpBySeason === null) {
            const h = await axios.get<HcpSeasonRow[]>(`${APIURL}/insights/handicaps-by-season`, { headers });
            setHcpBySeason(h.data);
          }
        } else {
          const res = await axios.get<LeagueInsights>(`${APIURL}/insights/league?season_year=${selectedSeason}`, { headers });
          setLeague(res.data);
        }
      } catch {
        setError('Failed to load league insights.');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSeason]);

  // Player insights — refetch on season or selected player change. "All seasons"
  // loads the year-over-year aggregate; a specific year loads the round timeline.
  useEffect(() => {
    if (!selectedSeason || selectedPlayerId == null) return;
    (async () => {
      setPlayerLoading(true);
      try {
        const headers = await authHeader();
        if (selectedSeason === ALL_SEASONS) {
          const res = await axios.get<PlayerSeasonAgg[]>(
            `${APIURL}/insights/player/${selectedPlayerId}/by-season`,
            { headers }
          );
          setPlayerBySeason(res.data);
        } else {
          const res = await axios.get<PlayerInsights>(
            `${APIURL}/insights/player/${selectedPlayerId}?season_year=${selectedSeason}`,
            { headers }
          );
          setPlayerData(res.data);
        }
      } catch {
        setPlayerData(null);
        setPlayerBySeason(null);
      } finally {
        setPlayerLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSeason, selectedPlayerId]);

  // Titles — lazy-loaded the first time the tab is opened (independent of the
  // season selector). Major titles come from a dedicated endpoint; league titles
  // are the standings champions of every completed season (season_end < today),
  // reusing /api/standings so they match the Standings page exactly.
  useEffect(() => {
    // Titles power both the Titles tab and the player Trophy case (Players tab,
    // all-seasons), so load them for either. Fetched once, then cached.
    if ((tab !== 'titles' && tab !== 'players') || majorTitles !== null) return;
    (async () => {
      setTitlesLoading(true);
      try {
        const headers = await authHeader();
        const majorRes = await axios.get<MajorTitles>(`${APIURL}/insights/major-titles`, { headers });
        setMajorTitles(majorRes.data);

        const today = new Date();
        const completed = seasons.filter((s) => s.season_end && new Date(s.season_end) < today);
        const standings = await Promise.all(
          completed.map((s) =>
            axios
              .get<StandingRow[]>(`${APIURL}/standings?season_year=${s.season_year}`, { headers })
              .then((r) => ({ year: s.season_year, rows: r.data }))
          )
        );

        // Tally a league title for the #1 (top points) player of each completed season.
        const tally = new Map<number, LeagueTitleRow>();
        for (const { year, rows } of standings) {
          const champ = rows[0];
          if (!champ) continue;
          const cur = tally.get(champ.player_id) || {
            player_id: champ.player_id,
            first_name: champ.first_name,
            last_name: champ.last_name,
            titles: 0,
            years: [],
          };
          cur.titles += 1;
          cur.years.push(year);
          tally.set(champ.player_id, cur);
        }
        const list = Array.from(tally.values()).sort(
          (a, b) => b.titles - a.titles || a.first_name.localeCompare(b.first_name)
        );
        setLeagueTitles(list);
      } catch {
        setError('Failed to load titles.');
      } finally {
        setTitlesLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  if (isLoading || loading) return <div className="ins-page"><style>{css}</style><div className="ins-loading">Loading insights…</div></div>;
  if (error) return <div className="ins-page"><style>{css}</style><div className="ins-err">{error}</div></div>;

  return (
    <div className="ins-page">
      <style>{css}</style>
      <div className="ins-inner">

      <div className="ins-top">
        <h1 className="ins-title">Insights</h1>
        {tab !== 'titles' && (
          <div className="ins-season">
            <span>Season</span>
            <select className="ins-select" value={selectedSeason} onChange={(e) => setSelectedSeason(e.target.value)}>
              <option value={ALL_SEASONS}>All seasons</option>
              {seasons.map((s) => (
                <option key={s.season_id} value={s.season_year}>{s.season_year}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="ins-tabs">
        <div className="ins-seg">
          <button className={tab === 'league' ? 'active' : ''} onClick={() => setTab('league')}>🏆 League</button>
          <button className={tab === 'players' ? 'active' : ''} onClick={() => setTab('players')}>⛳ Players</button>
          <button className={tab === 'titles' ? 'active' : ''} onClick={() => setTab('titles')}>👑 Titles</button>
        </div>
      </div>

      {tab === 'titles' ? (
        <TitlesTab leagueTitles={leagueTitles} majorTitles={majorTitles} loading={titlesLoading} />
      ) : tab === 'league' ? (
        selectedSeason === ALL_SEASONS ? (
          <LeagueYoYTab data={leagueBySeason} scoreMode={scoreMode} setScoreMode={setScoreMode} handicapsBySeason={hcpBySeason} />
        ) : (
          <LeagueTab data={league} scoreMode={scoreMode} setScoreMode={setScoreMode} />
        )
      ) : selectedSeason === ALL_SEASONS ? (
        <PlayersYoYTab
          players={players}
          selectedId={selectedPlayerId}
          onSelect={setSelectedPlayerId}
          data={playerBySeason}
          loading={playerLoading}
          majorTitles={majorTitles}
          leagueTitles={leagueTitles}
          currentIndex={selectedPlayerId != null ? liveIndex[selectedPlayerId] : undefined}
        />
      ) : (
        <PlayersTab
          players={players}
          selectedId={selectedPlayerId}
          onSelect={setSelectedPlayerId}
          data={playerData}
          loading={playerLoading}
        />
      )}
      </div>
    </div>
  );
};

export default Insights;
